import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, AlertTriangle, Info, CheckCircle2, AlertOctagon } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const SEV_ICON = { info: Info, warning: AlertTriangle, danger: AlertOctagon, success: CheckCircle2 };
const SEV_CLASS = { info: "text-sky-600", warning: "text-amber-600", danger: "text-rose-600", success: "text-emerald-600" };
const ROUTE = { lead: "/henvendelser/", project: "/prosjekter/", module: "/moduler/", quote: "/tilbud/", transport: "/prosjekter?tab=transport&id=", service: "/service?id=", issue: "/service?tab=avvik&id=", task: "/felt/oppgave/", calculation: "/tilbud?calc=" };

export const NotificationCenter = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: () => get("/notifications", { limit: 30 }), refetchInterval: 60_000 });
  const markAll = useMutation({ mutationFn: () => post("/notifications/read-all"), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
  const markOne = useMutation({ mutationFn: (id) => post(`/notifications/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
  const runRules = useMutation({ mutationFn: () => post("/notifications/run-rules"), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
  const unread = data?.unread || 0;

  const open = (n) => {
    markOne.mutate(n.id);
    const base = ROUTE[n.entity_type];
    if (base && n.entity_id) navigate(base + n.entity_id);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:bg-muted" aria-label="Varsler" title="Varsler" data-testid="notification-center-button">
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          {unread > 0 && <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 font-mono text-[10px] font-medium leading-none text-background tabular" data-testid="notification-unread-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <div className="text-[13px] font-medium">Varsler {unread > 0 && <span className="text-muted-foreground font-normal">({unread} uleste)</span>}</div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => runRules.mutate()} disabled={runRules.isPending} data-testid="notification-run-rules-button">Sjekk nå</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => markAll.mutate()} disabled={!unread} data-testid="notification-read-all-button"><CheckCheck className="h-3.5 w-3.5 mr-1" /> Merk alle</Button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {!data?.items?.length && <div className="py-10 text-center text-sm text-muted-foreground">Ingen varsler</div>}
          {data?.items?.map((n) => {
            const Icon = SEV_ICON[n.severity] || Info;
            return (
              <button key={n.id} onClick={() => open(n)} className={cn("flex w-full items-start gap-3 border-b px-3 py-2.5 text-left transition-colors duration-100 last:border-0 hover:bg-muted/50", !n.read && "bg-muted/30")} data-testid="notification-item">
                <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", SEV_CLASS[n.severity] || "text-muted-foreground")} />
                <div className="min-w-0 flex-1">
                  <div className={cn("text-[13px]", !n.read && "font-medium")}>{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(n.created_at)}</div>
                </div>
                {!n.read && <span className="mt-2 h-2 w-2 rounded-full bg-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
